import { Button, Icon } from '@toggle/design-system';
import { useDisableScroll } from '@toggle/helpers';
import { MarketOverviewWithNews, SingleNews } from '@toggle/toggle';
import { toZonedTime } from 'date-fns-tz';
import React, { useState } from 'react';
import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next';

import { ChatDeepAnalysis } from '~/components/chat/chat-input/chat-deep-analysis/ChatDeepAnalysis';
import { useMarketInsightsDrawer } from '~/hooks/use-market-insights-drawer/useMarketInsightsDrawer';
import { useChat } from '~/views/alfred/research/use-chat/useChat';
import { useChatNavigation } from '~/views/alfred/use-chat-navigation/useChatNavigation';
import { ArticleDrawerContent } from '~/widgets/news-widget/ArticleDrawerContent';

import { getMarketInsightTitle } from '../market-insight-card/utils';
import * as S from './MarketInsightDrawer.styles';

export const MarketInsightDrawer = ({
  activeInsightsGroup,
}: {
  activeInsightsGroup: MarketOverviewWithNews[];
}) => {
  const { t } = useTranslation([
    'whatsNew',
    'news',
    'chart',
    'analyze',
    'countries',
  ]);
  const [selectedNews, setSelectedNews] = useState<SingleNews | undefined>();
  const [activeArticleId, setActiveArticleId] = useState<string>('');

  useDisableScroll();
  const { navigateToConversation } = useChatNavigation();
  const { closeDrawer } = useMarketInsightsDrawer();
  const postQuestion = useChat(state => state.postQuestion);

  const onNewsPillClick = (news: SingleNews) => {
    setSelectedNews(news);
    setActiveArticleId(news.id);
  };

  const onGeneratedQuestionClick = async (question: string) => {
    const conversationId = (await postQuestion(question)) ?? '';
    navigateToConversation(conversationId);

    ReactGA.event({
      category: "What's new? question",
      action: 'Generated question clicked',
      label: `Question asked: ${question}`,
    });
  };

  const onCloseClick = () => {
    if (!!selectedNews) {
      setSelectedNews(undefined);
    } else {
      closeDrawer();
    }
  };

  const onBackgroundClick = () => {
    closeDrawer();
    setSelectedNews(undefined);
    setActiveArticleId('');
  };

  return (
    <S.StyledDrawer
      isOpen
      handleCloseClick={onCloseClick}
      customBackgroundClickHandler={onBackgroundClick}
      $isExpanded={!!selectedNews}
    >
      <S.Root $isDashboardUpdatesEnabled>
        <S.DrawersWrapper>
          <S.MarketInsightsWrapper>
            {activeInsightsGroup.map((insight, idx) => {
              const isLastItem = idx === activeInsightsGroup.length - 1;
              const isFirstItem = idx === 0;
              const zonedTime = toZonedTime(
                new Date(insight.date),
                'America/New_York'
              );
              return (
                <React.Fragment key={insight.id}>
                  <S.InsightWrapper>
                    {isFirstItem && (
                      <S.HeaderColumn>
                        <S.HeaderTitle>
                          {t('whatsNew:marketSummary')}
                        </S.HeaderTitle>
                        <S.RevampHeaderSubtitle>
                          {new Intl.DateTimeFormat(navigator.language, {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          }).format(zonedTime)}
                        </S.RevampHeaderSubtitle>
                      </S.HeaderColumn>
                    )}
                    <S.SummaryWrapper>
                      <S.SummaryTitle>
                        {getMarketInsightTitle(insight.market_hours, t).title}
                      </S.SummaryTitle>
                      <S.SummaryText>{insight.summary}</S.SummaryText>
                      {!!insight.articles.length && (
                        <>
                          <S.ReadMoreLabel>
                            {/* i18next-extract-mark-context-next-line ["benzinga", "mtn", "dowjones"] */}
                            {t('whatsNew:readMoreFrom', {
                              source: t('news:source', {
                                context: insight.articles[0].source,
                              }),
                            })}
                          </S.ReadMoreLabel>
                          <S.NewsPillsWrapper $isDashboardUpdatesEnabled>
                            {insight.articles.map(news => (
                              <S.NewsPill
                                onClick={() => onNewsPillClick(news)}
                                key={news.id}
                                $isDashboardUpdatesEnabled
                              >
                                <S.NewsPillText>{news.title}</S.NewsPillText>
                              </S.NewsPill>
                            ))}
                          </S.NewsPillsWrapper>
                        </>
                      )}
                    </S.SummaryWrapper>
                    <S.AlfredSectionWrapper>
                      <S.HorizontalDivider />
                      <S.AlfredSectionTitle>
                        <Icon
                          iconName="Alfred"
                          size={20}
                          fillColor="var(--icon-primary)"
                        />
                        <span>{t('whatsNew:whatAmIMissing')}</span>
                      </S.AlfredSectionTitle>
                      <S.AlfredQuestionsWrapper>
                        <S.StyledChatInput>
                          <ChatDeepAnalysis
                            config={{
                              placeholder: t('whatsNew:askAFollowUp'),
                              allowedModes: ['research'],
                              disableContextHeader: true,
                              autoFocus: false,
                            }}
                          />
                        </S.StyledChatInput>
                        <S.GeneratedQuestionsWrapper>
                          {insight.questions.map(question => (
                            <S.GeneratedQuestionWrapper
                              key={question}
                              onClick={() => onGeneratedQuestionClick(question)}
                            >
                              <span>{question}</span>
                              <Button iconName="ArrowUp" size="small" />
                            </S.GeneratedQuestionWrapper>
                          ))}
                        </S.GeneratedQuestionsWrapper>
                      </S.AlfredQuestionsWrapper>
                    </S.AlfredSectionWrapper>
                  </S.InsightWrapper>
                  {!isLastItem && (
                    <div>
                      <S.MarketInsightSeparator />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </S.MarketInsightsWrapper>
          {!!selectedNews && (
            <S.ArticleWrapper>
              <S.VerticalDivider />
              <ArticleDrawerContent
                newsId={activeArticleId}
                data={selectedNews}
              />
            </S.ArticleWrapper>
          )}
        </S.DrawersWrapper>
      </S.Root>
    </S.StyledDrawer>
  );
};
